import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { useStore } from '@/core/providers';
import type { FeedItem } from '@/types';

interface FeedCardProps {
  item: FeedItem;
  onClick: () => void;
}

function FeedCard({ item, onClick }: FeedCardProps) {
  const { settings } = useStore();
  const dateLocale = settings.language === 'zh' ? zhCN : enUS;

  const normalize = (value?: string | null) => (value ?? '').replace(/\s+/g, ' ').trim();

  const title = normalize(
    settings.language === 'zh' && item.title_zh ? item.title_zh : item.title
  );
  const summary = normalize(
    settings.language === 'zh' && item.summary_zh
      ? item.summary_zh
      : item.summary
  );

  return (
    <div
      onClick={onClick}
      className="relative overflow-hidden rounded-2xl border border-[var(--ff-border)] bg-[var(--ff-surface)] shadow-[0_12px_28px_rgba(0,0,0,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--ff-border)] hover:shadow-[0_16px_36px_rgba(0,0,0,0.08)] active:scale-[0.99] animate-slide-up cursor-pointer"
    >
      <div className="flex gap-4 p-4">
        <div className="flex-1 min-w-0 space-y-2">
          {(item.category || item.platform) && (
            <div className="inline-flex items-center gap-2">
              <span className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-telegram-text">
                {item.category || item.platform}
              </span>
              {item.sources_count && item.sources_count > 1 && (
                <span className="rounded-full bg-[var(--ff-surface-alt)] px-2 py-1 text-[11px] font-medium text-telegram-hint">
                  {item.sources_count} sources
                </span>
              )}
            </div>
          )}

          <h3 className="text-lg font-semibold text-telegram-text line-clamp-2">
            {title}
          </h3>

          {summary && (
            <p className="text-sm leading-relaxed text-telegram-hint line-clamp-2">
              {summary}
            </p>
          )}

          <div className="flex items-center gap-2 text-xs font-medium text-telegram-hint">
            <div className="flex items-center gap-1 rounded-full bg-[var(--ff-surface-alt)] px-2 py-1">
              <span className="w-2 h-2 rounded-full bg-[#0a84ff]" />
              <span>
                {formatDistanceToNow(new Date(item.published_at), {
                  addSuffix: true,
                  locale: dateLocale,
                })}
              </span>
            </div>
            {item.source && (
              <span className="rounded-full bg-[var(--ff-surface-alt)] px-2 py-1 text-telegram-hint">
                {item.source}
              </span>
            )}
          </div>
        </div>

        {item.image_url && (
          <div className="flex-shrink-0">
            <div className="relative h-24 w-24 overflow-hidden rounded-xl border border-[var(--ff-border)] bg-[var(--ff-surface-alt)]">
              <img
                src={item.image_url}
                alt=""
                className="h-full w-full object-cover transition duration-300"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FeedCard;
